<script setup>
import {ref, computed} from 'vue'
import {Link} from '@inertiajs/vue3'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Chart from 'primevue/chart'
import FormInput from "../../../widget/components/ui-components/FormInput.vue";
import {useForm} from "laravel-precognition-vue-inertia";
import {trans} from "laravel-vue-i18n";

const props = defineProps({
    worker: Object
})

const form = useForm('post', `/app/workers/${props.worker ? props.worker.id : 'new'}`,{
    id: props.worker?.id || 'new',
    name: props.worker?.name || '',
    note: props.worker?.note || ''
})

const chartData = computed(() => ({
    labels: [
        trans('common.months.jan'),
        trans('common.months.feb'),
        trans('common.months.mar'),
        trans('common.months.apr'),
        trans('common.months.may'),
        trans('common.months.jun'),
        trans('common.months.jul'),
        trans('common.months.aug'),
        trans('common.months.sep'),
        trans('common.months.oct'),
        trans('common.months.nov'),
        trans('common.months.dec'),
    ],
    datasets: [
        {
            label: trans('workers.chart_reservations'),
            data: props.worker?.stats?.by_months || [],
            fill: false,
            tension: 0.3
        }
    ]
}))

const chartOptions = {
    plugins: {
        legend: {
            labels: {
                color: '#495057'
            }
        }
    },
    scales: {
        x: {
            ticks: {
                color: '#6b7280'
            },
            grid: {
                color: '#f0f0f0'
            }
        },
        y: {
            ticks: {
                color: '#6b7280'
            },
            grid: {
                color: '#f0f0f0'
            }
        }
    }
}
</script>

<template>
    <div class="flex flex-col lg:flex-row gap-8 text-gray-800 dark:text-gray-200 ">
        <!-- LEVÝ OBSAH -->
        <div class="flex-1 space-y-8">
            <!-- HLAVICKA + STATISTIKY -->
            <div class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md w-full">
              <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                <i class="pi pi-user-edit text-fuchsia-500 !text-xl"></i>
                <div>
                  <p class="font-bold">{{ $t('workers.profile_title') }}</p>
                  <p class="text-sm">{{ $t('workers.profile_hint') }}</p>
                </div>
              </div>
              <div class="m-6 space-y-4">
                <FormInput :label="$t('workers.field_name')" :invalid="form.errors['name']" type="text" v-model="form.name"/>
                <FormInput type="textarea" :invalid="form.errors['note']" :label="$t('workers.field_note')" v-model="form.note" rows="3"/>

              </div>
            </div>
            <div v-if="worker" class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md">
                <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                    <i class="pi pi-gauge text-roori-blue !text-xl"></i>
                    <div>
                        <p class="font-bold">{{ $t('workers.stats_title') }}</p>
                        <p class="text-sm">{{ $t('workers.stats_hint', { year: new Date().getFullYear() }) }}</p>
                    </div>
                </div>
                <div class="m-6">
                    <Chart type="line" :data="chartData" :options="chartOptions" class="w-full"/>
                </div>
            </div>

            <!-- REZERVACE -->
            <div v-if="worker" class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm overflow-hidden backdrop-blur-md">
                <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                    <i class="pi pi-calendar text-amber-500 !text-xl"></i>
                    <div>
                        <p class="font-bold">{{ $t('workers.reservations_title') }}</p>
                        <p class="text-sm">{{ $t('workers.reservations_hint') }}</p>
                    </div>
                </div>
                <div>
                    <DataTable :value="worker.reservations" paginator :rows="6" responsiveLayout="scroll" class="text-sm">
                        <Column field="formatted_date" :header="$t('workers.col_date')"/>
                        <Column field="services.name" :header="$t('workers.col_service')"/>
                        <Column field="name" :header="$t('workers.col_client')"/>
                        <Column field="state" :header="$t('workers.col_state')">
                            <template #body="{ data }">
                                <Tag :value="data.state === 'accepted' ? $t('workers.state_accepted') : data.state === 'rejected' ? $t('workers.state_rejected') : $t('workers.state_pending')"
                                     :severity="data.state === 'accepted' ? 'success' : data.state === 'rejected' ? 'danger' : 'warning'"/>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- PRAVÝ PANEL -->
        <aside class="w-full lg:w-80 space-y-6">
            <div class="flex justify-end">
                <Button :label="$t('workers.btn_save')" fluid @click="form.submit" :loading="form.processing"/>
            </div>
            <div v-if="worker" class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md">
                <div class="m-6 text-sm">
                    <p><strong>{{ $t('workers.meta_created') }}</strong> {{ new Date(props.worker.created_at).toLocaleString() }}</p>
                    <p><strong>{{ $t('workers.meta_updated') }}</strong> {{ new Date(props.worker.updated_at).toLocaleString() }}</p>
                    <p v-if="props.worker.created_by"><strong>{{ $t('workers.meta_created_by') }}</strong> {{ props.worker.created_by }}</p>
                    <p v-if="props.worker.updated_by"><strong>{{ $t('workers.meta_updated_by') }}</strong> {{ props.worker.updated_by }}</p>
                </div>
            </div>

            <!-- SLUŽBY -->
            <div v-if="worker" class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md">
              <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                <i class="pi pi-briefcase text-indigo-500 !text-xl"></i>
                <div>
                  <p class="font-bold">{{ $t('workers.services_title') }}</p>
                  <p class="text-sm">{{ $t('workers.services_hint') }}</p>
                </div>
              </div>
              <div class="m-6 space-y-3">
                <div v-for="service in props.worker.services" :key="service.id"
                     class="flex justify-between items-center border-gray-100 dark:border-gray-700 pb-2">
                  <div>
                    <p class="font-medium">{{ service.name }}</p>
                    <p class="text-xs text-gray-500">{{ service.price || `${service.min_price} - ${service.max_price}` }} Kč</p>
                  </div>
                    <Link :href="`/app/services/${service.id}`">
                        <Button icon="pi pi-pencil"
                                class="p-button-text p-button-sm text-indigo-500"
                        />
                    </Link>
                </div>
                  <div class="opacity-70 italic text-sm text-center">{{ $t('workers.services_edit_note') }}</div>
              </div>
            </div>
        </aside>
    </div>
</template>

<style scoped lang="scss">
.bg-white\/80, .dark\:bg-slate-800\/60 {
    transition: all 0.25s ease;
}

.bg-white\/80:hover, .dark\:bg-slate-800\/60:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.p-datatable) {
    background: transparent;
    border: none;
}

:deep(.p-datatable-thead > tr > th) {
    font-weight: 600;
    font-size: 0.85rem;
    border-bottom: 1px solid var(--surface-border);
}

:deep(.p-datatable-tbody > tr > td) {
    border: none;
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
}
</style>
